import React from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, keepPreviousData } from '@tanstack/react-query'; //-tech-debt-react-query;

import { useFetchCiscoProducts } from '../../../../hooks/product-hooks';
import {
  normalizeItems,
  useFetchAllGoToMarkets,
  useFetchRagPipelineGroups,
  useFetchRagPipelinePurposes,
} from '../../../../hooks/rag-hooks';
import { fetchItems } from '../../../../support/queries';
import { DEFAULT_QUERY_OPTIONS } from '../../../../support/constants';

type IdValue = number | null | undefined;

type IdSelectProps = {
  value: IdValue;
  onChange: (value: number | null, row?: any | null) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
};

type ProductVersionSelectorProps = {
  productId?: number | null;
  value?: string | null;
  onChange: (value: string | null, row?: any | null) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
};

const toId = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const id = typeof value === 'object' ? value.id ?? value.Id : value;
  const numeric = Number(id);
  return Number.isFinite(numeric) ? numeric : null;
};

function versionValue(row: any): string {
  return String(
    row?.version ||
    row?.name ||
    row?.label ||
    row?.key ||
    row?.version_number ||
    row?.release ||
    row?.id ||
    '',
  );
}

function versionLabel(row: any): string {
  const value = versionValue(row);
  const label = row?.label || row?.name || value;
  return label ? String(label) : 'Version';
}

export const CiscoProductSelector: React.FC<IdSelectProps> = ({
                                                                value,
                                                                onChange,
                                                                label = 'Cisco Product',
                                                                disabled,
                                                                required,
                                                                helperText,
                                                              }) => {
  const [inputValue, setInputValue] = React.useState('');

  const query = useFetchCiscoProducts(
    {
      detailed: true,
      depth: 1,
      size: 250,
      vendor__is_cisco: true,
      'order-by': 'name',
      ...(inputValue.trim() ? { search: inputValue.trim() } : {}),
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const rows = normalizeItems<any>(query.data);

  const selected =
    rows.find(row => String(row.id) === String(value)) ||
    (value ? { id: value, name: `Product ${value}` } : null);

  return (
    <Autocomplete
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      onChange={(_, next) => onChange(toId(next), next || null)}
      options={rows}
      loading={query.isLoading || query.isFetching}
      disabled={disabled}
      getOptionLabel={option => option?.name || option?.label || `Product ${option?.id || ''}`}
      isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {query.isLoading || query.isFetching ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {option.id} / Offer: {option.offer_type || option.offer_type_key || '-'} / Version:{' '}
              {option.recommended_version || '-'}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
};

export const ProductVersionSelector: React.FC<ProductVersionSelectorProps> = ({
                                                                                productId,
                                                                                value,
                                                                                onChange,
                                                                                label = 'Product Version',
                                                                                disabled,
                                                                                required,
                                                                                helperText,
                                                                              }) => {
  const query = useQuery<any>({
    queryKey: ['product-versions', productId],
    queryFn: () =>
      fetchItems(
        'db/version/',
        {
          product: productId,
          detailed: true,
          depth: 1,
          size: 250,
          'order-by': '-release_date',
        },
        {
          pagination: true,
        },
      ),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(productId),
    placeholderData: keepPreviousData,
  });

  const rows = normalizeItems<any>(query.data);

  const selected =
    rows.find(row => versionValue(row) === String(value || '')) ||
    (value ? { id: value, version: value, name: value } : null);

  return (
    <Autocomplete
      value={selected}
      onChange={(_, next) => onChange(next ? versionValue(next) : null, next || null)}
      options={rows}
      loading={query.isLoading || query.isFetching}
      disabled={disabled || !productId}
      getOptionLabel={option => versionLabel(option)}
      isOptionEqualToValue={(option, val) => versionValue(option) === versionValue(val)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {query.isLoading || query.isFetching ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id || versionValue(option)}>
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {versionLabel(option)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {option.id || '-'} / Version: {versionValue(option)}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
};

export const GtmSelector: React.FC<IdSelectProps> = ({
                                                       value,
                                                       onChange,
                                                       label = 'Go-To-Market',
                                                       disabled,
                                                       required,
                                                       helperText,
                                                     }) => {
  const [inputValue, setInputValue] = React.useState('');

  const query = useFetchAllGoToMarkets(
    {
      detailed: true,
      depth: 1,
      size: 250,
      'order-by': 'name',
      ...(inputValue.trim() ? { search: inputValue.trim() } : {}),
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const rows = normalizeItems<any>(query.data);
  const selected = rows.find(row => String(row.id) === String(value)) || null;

  return (
    <Autocomplete
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      onChange={(_, next) => onChange(toId(next), next || null)}
      options={rows}
      loading={query.isLoading || query.isFetching}
      disabled={disabled}
      getOptionLabel={option => option?.name || option?.label || `GTM ${option?.id || ''}`}
      isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {query.isLoading || query.isFetching ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {option.id} / Type: {option.type || option.type_key || '-'}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
};

export const RagPipelineGroupSelector: React.FC<IdSelectProps> = ({
                                                                    value,
                                                                    onChange,
                                                                    label = 'Pipeline Group',
                                                                    disabled,
                                                                    required,
                                                                    helperText,
                                                                  }) => {
  const query = useFetchRagPipelineGroups(
    {
      enabled: true,
      size: 250,
      detailed: true,
      depth: 2,
      'order-by': 'name',
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const rows = normalizeItems<any>(query.data);
  const selected = rows.find(row => String(row.id) === String(value)) || null;

  return (
    <Autocomplete
      value={selected}
      onChange={(_, next) => onChange(toId(next), next || null)}
      options={rows}
      loading={query.isLoading || query.isFetching}
      disabled={disabled}
      getOptionLabel={option => option?.name || option?.key || `Group ${option?.id || ''}`}
      isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {query.isLoading || query.isFetching ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {option.name || option.key}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.key} / Active: {option.active_slot || '-'} / Ingest: {option.ingest_slot || '-'}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
};

export const RagPipelinePurposeSelector: React.FC<IdSelectProps> = ({
                                                                      value,
                                                                      onChange,
                                                                      label = 'Purpose',
                                                                      disabled,
                                                                      required,
                                                                      helperText,
                                                                    }) => {
  const query = useFetchRagPipelinePurposes(
    {
      is_active: true,
      size: 250,
      'order-by': 'sort_order',
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const rows = normalizeItems<any>(query.data);
  const selected = rows.find(row => String(row.id) === String(value)) || null;

  return (
    <Autocomplete
      value={selected}
      onChange={(_, next) => onChange(toId(next), next || null)}
      options={rows}
      loading={query.isLoading || query.isFetching}
      disabled={disabled}
      getOptionLabel={option => option?.label || option?.key || `Purpose ${option?.id || ''}`}
      isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {query.isLoading || query.isFetching ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {option.label || option.key}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.key}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
};